import { type CalendarConnection, type CalendarError, CalendarErrorType } from '../types/calendar';
import { googleCalendarService } from './googleCalendarService';
import { microsoftCalendarService } from './microsoftCalendarService';

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export interface ErrorHandlerOptions {
  retryConfig?: Partial<RetryConfig>;
  onRetryAttempt?: (attempt: number, error: CalendarError) => void;
  onMaxRetriesReached?: (error: CalendarError) => void;
}

export class CalendarErrorHandler {
  private static readonly DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    backoffMultiplier: 2,
  };

  private retryConfig: RetryConfig;
  private retryAttempts: Map<string, number> = new Map();

  constructor(options: ErrorHandlerOptions = {}) {
    this.retryConfig = {
      ...CalendarErrorHandler.DEFAULT_RETRY_CONFIG,
      ...options.retryConfig,
    };
  }

  /**
   * Handle calendar errors with automatic retry logic
   * Requirement 11.3: Add retry mechanisms for failed connections and sync operations
   */
  public async handleError<T>(
    operation: () => Promise<T>,
    errorContext: string,
    options: ErrorHandlerOptions = {},
  ): Promise<T> {
    const operationId = this.generateOperationId(errorContext);
    let lastError: CalendarError = this.createError(
      CalendarErrorType.SYNC_FAILED,
      `${errorContext}: Operation failed after retries`,
      false,
    );

    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        // Reset retry count on successful operation
        if (attempt > 0) {
          this.retryAttempts.delete(operationId);
        }

        return await operation();
      } catch (error) {
        lastError = this.normalizeError(error, errorContext);

        // Don't retry if error is not retryable
        if (!this.isRetryableError(lastError)) {
          throw lastError;
        }

        // Don't retry on last attempt
        if (attempt === this.retryConfig.maxRetries) {
          this.retryAttempts.delete(operationId);
          options.onMaxRetriesReached?.(lastError);
          throw lastError;
        }

        // Calculate delay for next retry
        const delay = this.calculateRetryDelay(attempt);

        // Track retry attempt
        this.retryAttempts.set(operationId, attempt + 1);
        options.onRetryAttempt?.(attempt + 1, lastError);

        console.warn(
          `[CalendarErrorHandler] Retry attempt ${attempt + 1}/${this.retryConfig.maxRetries} for ${errorContext} after ${delay}ms`,
          lastError,
        );

        // Wait before retrying
        await this.delay(delay);
      }
    }

    throw lastError;
  }

  /**
   * Handle connection errors with provider-specific retry logic
   * Requirement 11.2: Create error display components for connection failures
   */
  public async handleConnectionError(
    provider: 'google' | 'microsoft',
    operation: () => Promise<CalendarConnection>,
    userId: string,
  ): Promise<CalendarConnection> {
    const errorContext = `${provider}_connection_${userId}`;

    return this.handleError(operation, errorContext, {
      onRetryAttempt: (attempt, error) => {
        console.log(
          `[CalendarErrorHandler] Connection retry ${attempt} for ${provider}:`,
          error.message,
        );
      },
      onMaxRetriesReached: (error) => {
        console.error(
          `[CalendarErrorHandler] Max retries reached for ${provider} connection:`,
          error,
        );
        this.handleConnectionFailure(provider, error, userId);
      },
    });
  }

  /**
   * Handle sync errors with fallback to cached data
   * Requirement 11.3: Add retry mechanisms for failed connections and sync operations
   */
  public async handleSyncError<T>(
    provider: 'google' | 'microsoft',
    operation: () => Promise<T>,
    fallbackOperation: () => Promise<T>,
    userId: string,
  ): Promise<T> {
    const errorContext = `${provider}_sync_${userId}`;

    try {
      return await this.handleError(operation, errorContext, {
        retryConfig: {
          maxRetries: 2, // Fewer retries for sync operations
          baseDelay: 2000, // Longer initial delay
        },
        onRetryAttempt: (attempt, error) => {
          console.log(
            `[CalendarErrorHandler] Sync retry ${attempt} for ${provider}:`,
            error.message,
          );
        },
      });
    } catch (error) {
      console.warn(
        `[CalendarErrorHandler] Sync failed for ${provider}, falling back to cached data:`,
        error,
      );

      // Try fallback operation (cached data)
      try {
        return await fallbackOperation();
      } catch (fallbackError) {
        console.error(
          `[CalendarErrorHandler] Fallback also failed for ${provider}:`,
          fallbackError,
        );
        throw this.normalizeError(error, errorContext);
      }
    }
  }

  /**
   * Handle token refresh with fallback to re-authentication
   * Requirement 11.4: Build token refresh logic with fallback to re-authentication
   */
  public async handleTokenRefresh(
    connection: CalendarConnection,
    userId: string,
  ): Promise<CalendarConnection> {
    const provider = connection.provider.id;
    const errorContext = `${provider}_token_refresh_${userId}`;

    try {
      return await this.handleError(
        async () => {
          // Call the internal refresh method to avoid circular dependency
          if (provider === 'google') {
            return await googleCalendarService._refreshTokenInternal(connection);
          }
          if (provider === 'microsoft') {
            return await microsoftCalendarService._refreshTokenInternal(connection);
          }
          throw this.createError(
            CalendarErrorType.AUTH_FAILED,
            `Unsupported provider: ${provider}`,
            false,
            provider,
          );
        },
        errorContext,
        {
          retryConfig: {
            maxRetries: 2,
            baseDelay: 1500,
          },
          onMaxRetriesReached: (_error) => {
            console.error(
              `[CalendarErrorHandler] Token refresh failed, requiring re-authentication for ${provider}`,
            );
            this.handleReAuthenticationRequired(provider, userId);
          },
        },
      );
    } catch (_error) {
      // If token refresh fails completely, require re-authentication
      throw this.createError(
        CalendarErrorType.TOKEN_EXPIRED,
        'Token refresh failed, re-authentication required',
        false, // Not retryable - requires user action
        provider,
      );
    }
  }

  /**
   * Create a standardized calendar error
   */
  public createError(
    type: CalendarErrorType,
    message: string,
    retryable: boolean = true,
    provider?: 'google' | 'microsoft',
  ): CalendarError {
    return {
      type,
      message,
      retryable,
      provider,
    };
  }

  /**
   * Normalize various error types to CalendarError
   */
  private normalizeError(error: unknown, context: string): CalendarError {
    if (this.isCalendarError(error)) {
      return error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return this.createError(CalendarErrorType.NETWORK_ERROR, 'Network connection failed', true);
    }

    // Handle HTTP errors
    const httpError =
      typeof error === 'object' ? (error as { status?: number; statusText?: string }) : undefined;
    if (httpError?.status) {
      switch (httpError.status) {
        case 401:
          return this.createError(CalendarErrorType.AUTH_FAILED, 'Authentication failed', true);
        case 403:
          return this.createError(CalendarErrorType.PERMISSION_DENIED, 'Permission denied', false);
        case 429:
          return this.createError(CalendarErrorType.NETWORK_ERROR, 'Rate limit exceeded', true);
        case 500:
        case 502:
        case 503:
        case 504:
          return this.createError(CalendarErrorType.NETWORK_ERROR, 'Server error', true);
        default:
          return this.createError(
            CalendarErrorType.SYNC_FAILED,
            `HTTP ${httpError.status}: ${httpError.statusText || 'Unknown error'}`,
            true,
          );
      }
    }

    // Handle generic errors
    const message = error instanceof Error ? error.message : String(error);
    return this.createError(CalendarErrorType.SYNC_FAILED, `${context}: ${message}`, true);
  }

  /**
   * Check if an error is a CalendarError
   */
  private isCalendarError(error: unknown): error is CalendarError {
    if (error == null || typeof error !== 'object') {
      return false;
    }
    const candidate = error as Record<string, unknown>;
    return (
      typeof candidate.type === 'string' &&
      typeof candidate.message === 'string' &&
      typeof candidate.retryable === 'boolean'
    );
  }

  /**
   * Determine if an error should be retried
   */
  private isRetryableError(error: CalendarError): boolean {
    if (!error.retryable) {
      return false;
    }

    // Don't retry permission denied errors
    if (error.type === CalendarErrorType.PERMISSION_DENIED) {
      return false;
    }

    return true;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempt: number): number {
    const delay = this.retryConfig.baseDelay * this.retryConfig.backoffMultiplier ** attempt;
    return Math.min(delay, this.retryConfig.maxDelay);
  }

  /**
   * Generate unique operation ID for retry tracking
   */
  private generateOperationId(context: string): string {
    return `${context}_${Date.now()}`;
  }

  /**
   * Delay execution for specified milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Handle connection failure (can be overridden for custom behavior)
   */
  private handleConnectionFailure(
    provider: 'google' | 'microsoft',
    error: CalendarError,
    userId: string,
  ): void {
    console.error(
      `[CalendarErrorHandler] Connection failed for ${provider} user ${userId}:`,
      error,
    );

    // Could emit events here for UI to handle
    // this.eventEmitter.emit('connectionFailed', { provider, error, userId });
  }

  /**
   * Handle re-authentication requirement (can be overridden for custom behavior)
   */
  private handleReAuthenticationRequired(provider: 'google' | 'microsoft', userId: string): void {
    console.log(`[CalendarErrorHandler] Re-authentication required for ${provider} user ${userId}`);

    // Could emit events here for UI to handle
    // this.eventEmitter.emit('reAuthRequired', { provider, userId });
  }

  /**
   * Get current retry attempt count for an operation
   */
  public getRetryAttempt(operationId: string): number {
    return this.retryAttempts.get(operationId) || 0;
  }

  /**
   * Clear retry attempts for an operation
   */
  public clearRetryAttempts(operationId: string): void {
    this.retryAttempts.delete(operationId);
  }

  /**
   * Clear all retry attempts
   */
  public clearAllRetryAttempts(): void {
    this.retryAttempts.clear();
  }
}

// Export singleton instance
export const calendarErrorHandler = new CalendarErrorHandler();
